import { SignIn, SignUp } from "@clerk/clerk-react";
import { useEffect, useRef } from "react";
import { clerkAppearance } from "../lib/clerkAppearance";

type AuthModalProps = {
  isOpen: boolean;
  onClose: () => void;
  mode: "sign-in" | "sign-up";
  onModeChange?: (newMode: "sign-in" | "sign-up") => void;
};

export function AuthModal({ isOpen, onClose, mode, onModeChange }: AuthModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleLinkClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');
      
      if (link) {
        const href = link.getAttribute('href');
        const hrefValue = href ?? "";
        
        if (mode === 'sign-in' && hrefValue.includes('sign-up') && onModeChange) {
          event.preventDefault();
          event.stopPropagation();
          onModeChange('sign-up');
          return;
        }
        
        if (mode === 'sign-up' && hrefValue.includes('sign-in') && onModeChange) {
          event.preventDefault();
          event.stopPropagation();
          onModeChange('sign-in');
          return;
        }
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("mousedown", handleClickOutside);
      // Use capture phase to intercept before Clerk handles it
      document.addEventListener("click", handleLinkClick, true);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("click", handleLinkClick, true);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, mode, onModeChange]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-8">
      <div
        ref={modalRef}
        className="w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        {mode === "sign-in" ? (
          <SignIn
            appearance={clerkAppearance}
            routing="virtual"
            forceRedirectUrl="/"
          />
        ) : (
          <SignUp
            appearance={clerkAppearance}
            routing="virtual"
            forceRedirectUrl="/"
          />
        )}
      </div>
    </div>
  );
}
